import type { ReactNode } from "react";
import type { AssetControlView, FleetCommandRow } from "@/api/client";
import { ACT_LABEL, STATE_MEANS, commandLabel, levelLabel, reasonText,
         CommandStateChip as StateChip } from "@/modules/fleet/catalogue";
import { CollapsibleSection } from "@/components/Section";
import { ago, until } from "@/lib/format";

/** Everything about ONE asset, read-only: what state it is in, who is holding
 *  it, what firmware it is mid-way through, and what it has been told.
 *
 *  ITS ONE JOB IS TO NOT LIE ABOUT STATE, because three different things look
 *  like "immobilised" and only one means the vehicle cannot move:
 *
 *    HOLD PLACED, NOT YET APPLIED  the device has the command and is waiting
 *                                  for the vehicle to halt. It is STILL MOVING.
 *                                  Rendering this as "immobilised" is the
 *                                  error an operator would act on.
 *    APPLIED                       in force on the vehicle.
 *    HOLD PLACED, NOT ACTUATABLE   no connected part installed, so the lien
 *                                  stands and nothing can reach the asset.
 *
 *  And the EFFECTIVE TYPE is shown with the level that decided it, because the
 *  highest open level dictates the type (OFL-06k): a company that asked for
 *  WHEELLOCK can be getting FREEWHEEL while a financier hold stands, and a
 *  bare "immobilised" would hide that from both of them.
 *
 *  A COMPONENT RATHER THAN A PAGE. It was `/fleet/control`, its own subpage,
 *  until Dattatray said that made no sense — once the command page can target
 *  a single asset with the same filters, a separate page for one asset is a
 *  second way to do one thing (D-111, revised). So this is the detail that
 *  appears UNDER the target selector once one asset is chosen, and it holds no
 *  controls of its own: commanding lives in one place on the page.
 */
export function AssetDetail({ v }: { v: AssetControlView }) {
  const active = v.commands_active ?? v.commands ?? [];
  const archived = v.commands_archived ?? [];
  const fota = v.fota ?? [];

  return (
    <>
      <Banner v={v} />

      {/* ONE LINE, THEN DISCLOSURE. Selecting an asset used to unfold five
          tables at once -- holds, firmware, active, archived, and the page's
          own batches underneath -- and Dattatray's verdict was the right one:
          *"This is just too much info to indicate when I select a singkle
          asset"*. What an operator needs at the instant of commanding is the
          BANNER (can this vehicle move) and a count of what is outstanding.
          Everything else is context for when something looks wrong, so it is
          one click away rather than scrolling past.

          The counts stay visible even when collapsed, which is the point: a
          closed section still says "Holds (2)". */}
      {/* THE SUMMARY LINE IS GONE WHEN IT SAYS NOTHING. Dattatray, on
          "Nothing outstanding · no holds": *"remove this"*. He is right - on a
          healthy asset it restated the green bar above it in two more
          negatives, and the collapsed sections below already carry their own
          counts ("Holds (0)"). Three ways of saying the same nothing.
          It still appears when there IS something, because "2 commands
          outstanding" above a collapsed section is the one case where the line
          earns its space. */}
      {(active.length > 0 || v.holds.length > 0 || fota.length > 0) && (
        <div className="cap" style={{ margin: "10px 2px 2px" }}>
          {active.length > 0 && (
            <><b>{active.length}</b> command{active.length === 1 ? "" : "s"} outstanding</>
          )}
          {active.length > 0 && v.holds.length > 0 && " · "}
          {v.holds.length > 0 && (
            <><b>{v.holds.length}</b> hold{v.holds.length === 1 ? "" : "s"}</>
          )}
          {fota.length > 0 && <> · firmware history ({fota.length})</>}
        </div>
      )}

      {/* OPEN WHEN THERE IS SOMETHING TO ACT ON, closed when there is not --
          an empty table that has to be collapsed by hand is the same noise in
          a different shape. */}
      <CollapsibleSection title="Holds" count={v.holds.length}
        defaultOpen={v.holds.length > 0}>
        <table className="tbl">
          <thead><tr>
            <th>Level</th><th>Type</th><th>Placed by</th><th>When</th><th>Note</th>
          </tr></thead>
          <tbody>
            {v.holds.length === 0 && (
              <tr><td colSpan={5} className="cap">No holds.</td></tr>
            )}
            {v.holds.map((h) => (
              <tr key={h.level}>
                <td>{levelLabel(h.level)}</td>
                <td>{ACT_LABEL[h.action]}</td>
                <td className="mono">{h.placed_by}</td>
                <td>{h.placed_at ? `${ago(h.placed_at)} ago` : "—"}</td>
                <td>{h.note ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CollapsibleSection>

      {/* "OUTSTANDING" / "FINISHED", not "Active" / "Archived". Those words
          are the RUN list's, and reusing them one block below for a single
          asset is the double indication Dattatray flagged -- the same two
          words naming two different scopes. */}
      <CollapsibleSection title="Outstanding" count={active.length}
        defaultOpen={active.length > 0}>
        <CommandTable rows={active} showExpiry empty="Nothing outstanding." />
      </CollapsibleSection>

      <CollapsibleSection title="Finished" count={archived.length}>
        <CommandTable rows={archived} empty="Nothing has finished yet." />
      </CollapsibleSection>

      {fota.length > 0 && (
        <CollapsibleSection title="Firmware" count={fota.length}>
          <table className="tbl">
            <thead><tr>
              <th>Rollout</th><th>Target</th><th>Node</th><th>State</th>
              <th>Attempts</th><th>Converged</th>
            </tr></thead>
            <tbody>
              {fota.map((f, i) => (
                <tr key={i}>
                  <td>{f.batch}</td>
                  <td className="num">v{f.target_version}</td>
                  <td>{f.software}</td>
                  <td>{f.state}</td>
                  <td className="num">{f.attempts}</td>
                  <td>{f.converged_at ? `${ago(f.converged_at)} ago` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CollapsibleSection>
      )}
    </>
  );
}

function CommandTable({ rows, empty, showExpiry = false }: {
  rows: FleetCommandRow[]; empty: string; showExpiry?: boolean;
}) {
  return (
    <div className="tblwrap">
      <table className="tbl">
        <thead><tr>
          <th>When</th><th>Level</th><th>Command</th><th>State</th>
          <th>Means</th><th>Reason</th>
          {showExpiry && <th title="when Orbit stops retrying delivery">Retries until</th>}
          <th>By</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={showExpiry ? 8 : 7} className="cap">{empty}</td></tr>
          )}
          {rows.map((c) => (
            <tr key={c.cmd_id}>
              <td>{c.created_at ? `${ago(c.created_at)} ago` : "—"}</td>
              <td>{c.level != null ? levelLabel(c.level) : "—"}</td>
              <td>{commandLabel(c.action)}</td>
              <td><StateChip c={c} /></td>
              <td style={{ fontSize: 12 }}>{STATE_MEANS[c.state] ?? "—"}</td>
              <td>{reasonText(c.reason_code)}</td>
              {showExpiry && (
                // NAMED "Orbit retries until", not "expires": the restraint
                // does NOT lift at this time. A hold lives until it is
                // released, and labelling this "expires" would tell an
                // operator a locked vehicle frees itself (OFL-06c).
                <td title="when Orbit stops retrying delivery. The hold itself does not lift">
                  {/* `until`, not `ago`: `ago` is clamped at zero for a
                      past-tense reading, so a deadline 24 hours out rendered
                      as "in 0s". */}
                  {c.expires_at ? `in ${until(c.expires_at)}` : "—"}
                </td>
              )}
              <td className="mono" style={{ fontSize: 11 }}>{c.created_by}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** THE WHOLE BAR CARRIES THE STATE, not a 3px stripe on its edge.
 *
 *  Dattatray: *"can we not just write Asset is Mobilied / Its simple with the
 *  entire bar green!"* He is right on both halves. It read "MOBILE. No holds on
 *  this asset." against a white panel with a thin green edge: a shouted
 *  adjective, then a second sentence restating it in the negative, and a colour
 *  you had to look for. The state of a vehicle is the one thing on this page
 *  that should be readable without reading.
 *
 *  A TINT, NOT A FILL. `color-mix` at 12% keeps the text at full contrast in
 *  both themes, where a saturated background would force white text that then
 *  fails on the light one. Same construction the command buttons use.
 */
function StateBar({ tone, children }: { tone: string; children: ReactNode }) {
  return (
    <div className="panel panel-pad" style={{
          border: `1px solid ${tone}`,
          background: `color-mix(in srgb, ${tone} 12%, transparent)` }}>
      {children}
    </div>
  );
}

/** How long ago the vehicle last reported, stated and never judged.
 *
 *  Dattatray: *"We keep orbit simple and plain: last known state and staleness
 *  of the state that is it!"* So no live/stale threshold, no UNKNOWN banner,
 *  no colour that changes with age. Five minutes is unremarkable for a bike in
 *  a depot and alarming for one mid-ride, and only the person looking knows
 *  which they have.
 *
 *  It also makes DISAGREEMENT visible without Orbit adjudicating it: a state
 *  reported seconds ago, above a Holds section naming a level that holds this
 *  asset, is a contradiction a reader can see. Orbit does not have to call it.
 */
function Reported({ at }: { at: string | null }) {
  return (
    <span style={{ fontSize: 11.5, color: "var(--t-mute)", marginLeft: 8,
                   fontWeight: 400 }}>
      {at ? `reported ${ago(at)} ago` : "never reported"}
    </span>
  );
}

function Banner({ v }: { v: AssetControlView }) {
  const armed = v.commands.find((c) => c.state === "received");
  if (!v.immobile) {
    return (
      <StateBar tone="var(--ok)">
        <b style={{ color: "var(--ok)", fontSize: 13.5 }}>Asset is mobile</b>
        <Reported at={v.last_reported_at} />
      </StateBar>
    );
  }
  // IMMOBILE per Orbit's holds. Now say which of the three it actually is.
  const applied = v.commands.some((c) => c.state === "applied");
  const colour = applied ? "var(--bad)" : "var(--warn)";
  return (
    <StateBar tone={colour}>
      <b style={{ color: colour, fontSize: 13.5 }}>
        {applied ? "Asset is immobilised"
          : armed ? "Immobilise armed, not yet applied"
          : "Hold placed"}
      </b>
      <Reported at={v.last_reported_at} />
      {": "}
      {ACT_LABEL[v.effective_action]}
      {v.effective_level != null && (
        <> , decided by <b>{levelLabel(v.effective_level)}</b></>
      )}
      {!applied && armed && (
        <div style={{ marginTop: 6, fontSize: 12.5 }}>
          The device has the command and is waiting for the vehicle to come to a
          halt (controller data valid, speed and RPM zero, for 5 s).
          <b> The vehicle can still move until then.</b>
        </div>
      )}
      {!v.actuatable && (
        <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--warn)" }}>
          No connected part is installed on this asset, so the hold is recorded
          but nothing can be sent to it.
        </div>
      )}
    </StateBar>
  );
}
